// preview-bot.tsx
//
// Dry-run panel behind the studio's "Preview Bot" button. The operator types
// the message that should fire the trigger, presses Try it out, and walks the
// flow — answering wherever the bot asks for a reply.
//
// This panel SENDS REAL MESSAGES. POST /journeys/preview/send walks the
// canvas and dispatches each step through the same celery path a published
// journey uses, so what the operator receives is what a customer would.
// It still needs no publish — the definition comes from the live canvas.
//
// A recipient is therefore required before a run can start; the endpoint
// refuses without one so a test can never reach a real customer by accident.
//
// State model mirrors the backend's: the panel holds the inbound message and
// the list of replies given so far, and re-posts the whole definition on every
// change. There is no session id to keep in sync — a Reset is just clearing
// two arrays.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Check, Clock, CornerDownLeft, Eye, GitBranch, Maximize2,
  MessageSquare, Minimize2, Play, RotateCcw, X, Zap,
} from "lucide-react";
import useJouneyReq from "../../hook/bot-req";
import { useTenantId } from "../../hook/use-tenant-id";
import { ActionNode, TriggerNode } from "../../../views/convoauto-pages/bot-studio/types";
import { actionNodesToRcsFlow, buildJourneyPayload } from "../../../views/convoauto-pages/bot-studio/constant";
import { TemplateModule } from "../../../views/marketing-pages/templates";

type StepStatus = "matched" | "delivered" | "waiting" | "not_reached" | "skipped";

interface PreviewStep {
  id: string;
  type: string;
  label: string;
  status: StepStatus;
  at_seconds?: number | null;
  detail?: string | null;
  branch?: string | null;
}
interface TimelineEntry { at: string; text: string; tone: "neutral" | "success" | "warning" }
interface Awaiting { step_id: string; prompt: string; expires_in_seconds: number }
// Mirrors InboundReply in routes/preview_send.py — what the handset really
// sent. `payload` is the button id a Branch step matches on, which a tap
// carries instead of text.
interface InboundReply { from_phone: string; kind: string; text: string; payload?: string | null; at: string }
interface PreviewResult {
  status: string;
  steps: PreviewStep[];
  timeline: TimelineEntry[];
  awaiting: Awaiting | null;
  variables: Record<string, unknown>;
  session_ttl_seconds: number;
}

const CHANNELS = ["WhatsApp", "RCS", "SMS"] as const;

// Card chrome per status. Only three visual states carry colour — a run
// should read at a glance as "done / needs me / never got there".
const STATUS_STYLE: Record<StepStatus, string> = {
  matched:     "border-green-500 bg-white",
  delivered:   "border-green-500 bg-white",
  waiting:     "border-amber-400 bg-amber-50",
  not_reached: "border-gray-200 bg-white",
  skipped:     "border-gray-200 bg-gray-50",
};

const STATUS_TEXT: Record<StepStatus, string> = {
  matched:     "text-green-700",
  delivered:   "text-green-700",
  waiting:     "text-amber-700",
  not_reached: "text-[#7B7B7B] font-semibold",
  skipped:     "text-[#7B7B7B] font-semibold",
};

// Step type -> the badge shown on the card, matching the legend row.
const TYPE_META: Record<string, { label: string; Icon: React.ElementType }> = {
  trigger:            { label: "TRIGGER",      Icon: Zap },
  send_message:       { label: "SEND MESSAGE", Icon: MessageSquare },
  whatsapp_message:   { label: "SEND MESSAGE", Icon: MessageSquare },
  send_email:         { label: "SEND MESSAGE", Icon: MessageSquare },
  wait_for_reply:     { label: "AWAIT REPLY",  Icon: MessageSquare },
  wait_till:          { label: "DELAY",        Icon: Clock },
  condition:          { label: "CONDITION",    Icon: GitBranch },
};

// Masked recipient, mirroring how a real conversation header shows it —
// enough digits to recognise the number, not enough to read it over a
// shoulder during a demo.
const maskPhone = (raw: string): string => {
  const d = (raw || "").replace(/\D/g, "");
  if (d.length < 7) return raw || "";
  return `+${d.slice(0, 3)} ${d.slice(3, 5)}\u2022\u2022\u2022 ${d.slice(-4)}`;
};

const metaFor = (type: string) =>
  TYPE_META[type] ?? { label: (type || "STEP").replace(/_/g, " ").toUpperCase(), Icon: MessageSquare };

const statusLabel = (s: PreviewStep): string => {
  const at = typeof s.at_seconds === "number"
    ? `${Math.floor(s.at_seconds / 60)}:${String(s.at_seconds % 60).padStart(2, "0")}`
    : "";
  switch (s.status) {
    case "matched":   return `Matched at ${at}`;
    case "delivered": return `Delivered at ${at}`;
    case "waiting":   return "Stuck · no reply yet";
    case "skipped":   return "Not taken";
    default:          return "Not reached";
  }
};

// One step card. Shared by the single-column flow and the paired
// condition branches so both render identically.
const StepCard: React.FC<{
  step: PreviewStep;
  branchLabel?: string;
  body?: string;
  /** What actually happened on the wire for this step, if anything. */
  delivery?: { sent: boolean; channel: string; to?: string; reason?: string };
}> = ({ step: s, branchLabel, body, delivery }) => {
  const { label, Icon } = metaFor(s.type);
  const dim = s.status === "not_reached" || s.status === "skipped";
  const [open, setOpen] = useState(false);
  return (
    <div className={`flex items-start gap-3 rounded-xl border-2 p-3 ${
      branchLabel ? "" : "mb-3"} ${STATUS_STYLE[s.status]}`}>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <Icon size={14} className="text-gray-500" />
          <span className="text-[11px] font-semibold tracking-wide text-gray-500">
            {branchLabel ? `${label} · ${branchLabel}` : label}
          </span>
        </div>
        <p className={`text-xs font-medium ${STATUS_TEXT[s.status]}`}>{statusLabel(s)}</p>
        <p className={`mt-0.5 truncate text-sm ${dim ? "text-[#7B7B7B] font-semibold" : "text-gray-800"}`}>
          {s.label}
        </p>
        {s.detail && <p className="mt-0.5 text-[11px] text-[#7B7B7B] font-semibold">{s.detail}</p>}
        {delivery && (
          // Separate from the step status on purpose: a step can run in the
          // flow and still fail to send (unsupported channel, no WABA), and
          // collapsing the two would hide exactly that case.
          <p className={`mt-1 text-[11px] ${delivery.sent ? "text-green-700" : "text-amber-700"}`}>
            {/* "Queued", not "Delivered". All the API knows is that the send
                was accepted onto the worker queue; the wrapper returns 200 for
                everything and Meta can still reject downstream. Claiming
                delivery here is what let the Vouchlinks failures read green
                for a week. The handset is the only proof. */}
            {delivery.sent
              ? `Queued to ${delivery.channel} · ${delivery.to} — check the handset`
              : `Not sent · ${delivery.reason || "unsupported"}`}
          </p>
        )}
        {body && open && (
          // The actual template copy. Seeing the words that would reach a
          // customer is the point of a preview — a template name alone
          // does not tell you whether the message reads correctly.
          <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 p-2 font-sans text-[11px] leading-relaxed text-gray-600">
            {body}
          </pre>
        )}
      </div>
      {(s.status === "delivered" || s.status === "matched") && (
        <Check size={16} className="mt-0.5 flex-shrink-0 text-green-600" />
      )}
      {s.status === "waiting" && (
        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-500" />
      )}
    </div>
  );
};

// Rows to render: mostly single cards, but the two steps following a
// condition are paired so they sit side by side.
type Row =
  | { kind: "single"; step: PreviewStep }
  | { kind: "branch"; a: PreviewStep; b: PreviewStep };

const buildRows = (steps: PreviewStep[]): Row[] => {
  const rows: Row[] = [];
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    rows.push({ kind: "single", step: s });
    // A condition's two outcomes are the next two steps. Pair them only
    // when both exist and neither is itself a condition, so a nested
    // condition still renders on its own line.
    if (s.type === "condition") {
      const a = steps[i + 1];
      const b = steps[i + 2];
      if (a && b && a.type !== "condition" && b.type !== "condition") {
        rows.push({ kind: "branch", a, b });
        i += 2;
      }
    }
  }
  return rows;
};

interface Props {
  open: boolean;
  onClose: () => void;
  botName: string;
  triggerNode: TriggerNode | null;
  actionNodes: ActionNode[];
  templates: TemplateModule[];
  /** RCS bots are authored as {nodes, edges}; the backend normalises them. */
  isRcs?: boolean;
}

const PreviewBotPanel: React.FC<Props> = ({
  open, onClose, botName, triggerNode, actionNodes, templates, isRcs = false,
}) => {
  const [channel, setChannel] = useState<string>(isRcs ? "RCS" : "WhatsApp");
  const [inbound, setInbound] = useState("hi");
  // Simulated recipient. Shown masked in the session panel so a run reads
  // like a real conversation; nothing is ever sent to it.
  // Real recipient. There is no simulate mode — Anshul 2026-08-10: the
  // operator should receive real messages. So this is required before a
  // run can start, and the run button says so rather than failing later.
  // The recipient comes from Settings -> Testing & Preview, where it has
  // been verified by OTP. Deliberately NOT typed here: a test send reaches
  // a real handset, and a number retyped on every run is one slip away
  // from messaging a stranger. A verified number on file cannot be.
  const [testPhone, setTestPhone] = useState("");
  // Settings holds a phone only. Email stays in the request shape because
  // the endpoint accepts either and email-only flows may want it later.
  const testEmail = "";
  const [numberLoading, setNumberLoading] = useState(true);
  const [botTestingAllowed, setBotTestingAllowed] = useState(true);
  const [sent, setSent] = useState<any[]>([]);
  // Two different things, deliberately kept apart. `replies` is the list of
  // reply *texts* that drives the flow — it is what POST /preview/send
  // replays to walk past await-reply steps, so it must stay string[].
  // `inboundReplies` is what the handset actually sent, as objects with a
  // kind and a button payload. Holding both in one state made the poll
  // overwrite the flow-driving list with objects, which would then be
  // POSTed back where strings were expected.
  const [replies, setReplies] = useState<string[]>([]);
  const [inboundReplies, setInboundReplies] = useState<InboundReply[]>([]);
  // Real replies already folded into `replies`, keyed by timestamp+text, so
  // an 8-second poll does not re-apply the same tap on every tick.
  const consumedRef = useRef<Set<string>>(new Set());
  const [draftReply, setDraftReply] = useState("");
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string>("");
  const [runs, setRuns] = useState(0);
  // Maximised gives long flows the full viewport — a bot with a dozen
  // steps does not fit the dialog without heavy scrolling.
  const [maximized, setMaximized] = useState(false);
  // Seconds left on the simulated session. Ticks only while the bot is
  // parked waiting, which is the only time a real session would be
  // burning its window — a completed run has nothing left to expire.
  const [ttlLeft, setTtlLeft] = useState<number | null>(null);

  const tenantId = useTenantId();
  const token = localStorage.getItem("authToken");
  // Always the real-send endpoint. It returns the same trace the
  // simulator would, plus what was actually delivered, so one call
  // drives the flow cards and the delivery column together.
  const { makeRequest: runPreview, loading } = useJouneyReq("/journeys/preview/send", "POST", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: fetchReplies } = useJouneyReq("/journeys/preview/replies", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: fetchTestNumber } = useJouneyReq("/settings/test-number", "GET", {
    Authorization: `Bearer ${token}`,
  });

  // Load the verified number each time the panel opens rather than once on
  // mount: the operator may have just set it in Settings and come straight
  // back, and a stale empty value would tell them to go configure something
  // they already configured.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setNumberLoading(true);
      try {
        const [res] = await fetchTestNumber();
        if (cancelled) return;
        if (res?.verified && res?.phone_number) {
          setTestPhone(res.phone_number);
          setBotTestingAllowed(res.bot_testing !== false);
        } else {
          setTestPhone("");
        }
      } catch {
        if (!cancelled) setTestPhone("");
      } finally {
        if (!cancelled) setNumberLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The definition we simulate is built from the live canvas, not from the
  // saved journey — previewing an unsaved draft is the whole point.
  const definition = useMemo(() => {
    try {
      if (isRcs) {
        // RCS is authored in a different shape and served by a different
        // backend, so build its native flow; the preview endpoint
        // normalises {nodes, edges} into the same step list.
        return actionNodesToRcsFlow(botName, triggerNode, actionNodes) as any;
      }
      const payload: any = buildJourneyPayload(
        botName, triggerNode, actionNodes, templates, { channel },
      );
      return payload?.definition ?? payload ?? {};
    } catch {
      return {};
    }
  }, [botName, triggerNode, actionNodes, templates, channel, isRcs]);

  const execute = useCallback(async (nextReplies: string[]) => {
    setError("");
    const [res] = await runPreview({
      definition, channel, inbound, replies: nextReplies, variables: {},
      tenant_id: tenantId,
      recipient: { phone: testPhone.trim(), email: testEmail.trim() },
    });
    if (!res || res.detail) {
      setError(typeof res?.detail === "string" ? res.detail : "Could not run this flow.");
      setResult(null);
      return;
    }
    // The endpoint returns the trace under trace_steps; map it onto the
    // same shape the cards already render so nothing downstream changes.
    setResult({
      status: res.trace_status || res.status,
      steps: res.trace_steps || [],
      timeline: res.timeline || [],
      awaiting: res.awaiting ?? null,
      variables: {},
      session_ttl_seconds: res.session_ttl_seconds ?? 600,
    } as PreviewResult);
    setSent(res.steps || []);
  }, [runPreview, definition, channel, inbound, tenantId, testPhone, testEmail]);

  // Poll for what the recipient sent back — a button tap does not arrive
  // as text, so this is the only way to confirm an interactive step
  // actually worked.
  const loadReplies = useCallback(async () => {
    const phone = testPhone.trim();
    if (!phone) return;
    const [res] = await fetchReplies(undefined, { phone, minutes: 30 });
    const incoming: InboundReply[] = res?.replies || [];
    setInboundReplies(incoming);

    // A real tap has to move the flow on, not just appear in a list —
    // otherwise a button step sits on "waiting" forever even though the
    // operator answered on the handset. A Branch matches the button's
    // payload id, so prefer that over the visible label.
    const fresh = incoming.filter((r) => {
      const key = `${r.at}|${r.payload || r.text}`;
      if (consumedRef.current.has(key)) return false;
      consumedRef.current.add(key);
      return true;
    });
    if (fresh.length) {
      const next = [...replies, ...fresh.map((r) => r.payload || r.text)];
      setReplies(next);
      await execute(next);
    }
  }, [fetchReplies, testPhone, replies, execute]);

  // Blocked either when no verified number exists, or when the tenant has
  // switched Bot Testing off for it — the toggle is a real permission, not
  // decoration, so honour it here rather than sending anyway.
  const canRun = Boolean(testPhone.trim()) && botTestingAllowed;

  const handleTryItOut = async () => {
    if (!canRun) return;
    setRuns((n) => n + 1);
    setReplies([]);
    setInboundReplies([]);
    consumedRef.current.clear();
    setDraftReply("");
    await execute([]);
  };

  const handleSendReply = async () => {
    const text = draftReply.trim();
    if (!text) return;
    const next = [...replies, text];
    setReplies(next);
    setDraftReply("");
    await execute(next);
  };

  const handleReset = () => {
    setReplies([]);
    setInboundReplies([]);
    consumedRef.current.clear();
    setDraftReply("");
    setResult(null);
    setError("");
    setSent([]);
  };

  // Re-running on channel change keeps the panel honest: switching channel
  // can change which steps are even reachable.
  useEffect(() => {
    if (result) execute(replies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  // Reset the countdown whenever a run produces a new state.
  useEffect(() => {
    if (!result) { setTtlLeft(null); return; }
    setTtlLeft(result.session_ttl_seconds ?? 600);
  }, [result]);

  // Tick down only while parked awaiting a reply. Reaching zero is a real
  // outcome the operator should see — it is what would close the session
  // on a live conversation.
  useEffect(() => {
    if (ttlLeft === null || ttlLeft <= 0) return;
    if (result?.status !== "waiting_for_reply") return;
    const t = setInterval(() => setTtlLeft((s) => (s === null ? null : Math.max(0, s - 1))), 1000);
    return () => clearInterval(t);
  }, [ttlLeft, result?.status]);

  // Pull replies as soon as the bot is waiting on one, then on a timer —
  // the operator should not have to guess when to press Refresh.
  useEffect(() => {
    if (!open || result?.status !== "waiting_for_reply") return;
    loadReplies();
    const t = setInterval(loadReplies, 8000);
    return () => clearInterval(t);
  }, [open, result?.status, loadReplies]);

  useEffect(() => {
    if (!open) return;
    // Escape restores down before it closes — otherwise the reflex press
    // after maximising throws away the run you were reading.
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (maximized) setMaximized(false);
      else onClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose, maximized]);

  if (!open) return null;

  const steps = result?.steps ?? [];
  const rows = buildRows(steps);

  // Body to show behind a card's "Preview" link. Matches the card label
  // against the templates the studio already loaded, so no extra fetch —
  // and falls back to nothing rather than guessing.
  const bodyFor = (s: PreviewStep): string | undefined => {
    const name = (s.label || "").trim();
    if (!name) return undefined;
    const t: any = (templates || []).find(
      (x: any) => (x?.template_name || "").toLowerCase() === name.toLowerCase()
    );
    const body = t?.content || t?.body || "";
    return typeof body === "string" && body.trim() ? body.trim() : undefined;
  };
  const awaiting = result?.awaiting ?? null;
  const ttl = result?.session_ttl_seconds ?? 600;

  return (
    <div className={`fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 ${
           maximized ? "p-0" : "p-4"}`}
         onClick={onClose}>
      <div className={`flex flex-col overflow-hidden bg-white shadow-2xl ${
             maximized
               ? "h-full w-full rounded-none"
               : "max-h-[92vh] w-full max-w-5xl rounded-xl"}`}
           onClick={(e) => e.stopPropagation()}>

        {/* ── Toolbar ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            <RotateCcw size={14} /> Reset
          </button>

          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {CHANNELS.map((c) => (
              <button
                key={c}
                onClick={() => setChannel(c)}
                className={`rounded-md px-3 py-1 text-sm transition-colors ${
                  channel === c
                    ? "bg-white font-medium text-indigo-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <Clock size={13} />
              {runs} preview {runs === 1 ? "run" : "runs"} on this bot
            </span>
            {/* Read-only: the number is set and OTP-verified in Settings.
                Showing it here (rather than an input) makes it obvious where
                a real message is about to land, and removes any chance of a
                mistyped recipient. */}
            {numberLoading ? (
              <span className="text-xs text-[#7B7B7B] font-semibold">Loading test number…</span>
            ) : testPhone ? (
              <span className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm">
                <Check size={13} className="text-green-600" />
                <span className="font-mono text-gray-700">{testPhone}</span>
                {!botTestingAllowed && (
                  <span className="text-[11px] text-amber-700">· Bot Testing off</span>
                )}
              </span>
            ) : (
              <span className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                No verified test number — set one in Settings › Testing &amp; Preview
              </span>
            )}
            <button
              onClick={handleTryItOut}
              disabled={loading || !canRun}
              title={canRun ? `Deliver this flow's messages to ${testPhone}`
                     : !testPhone ? "Set a verified test number in Settings › Testing & Preview"
                     : "Bot Testing is switched off for this number in Settings"}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
            >
              <Play size={14} /> {loading ? "Sending…" : "Send test"}
            </button>
            <button
              onClick={() => setMaximized((m) => !m)}
              className="rounded-lg p-1.5 text-[#7B7B7B] font-semibold transition-colors hover:bg-gray-100 hover:text-gray-600"
              title={maximized ? "Restore down" : "Maximise"}
              aria-label={maximized ? "Restore preview" : "Maximise preview"}
            >
              {maximized ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
            </button>
            <button onClick={onClose}
                    className="rounded-lg p-1.5 text-[#7B7B7B] font-semibold transition-colors hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close preview">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Legend ──────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-4 border-b border-gray-100 px-4 py-2">
          {[
            { label: "Trigger", Icon: Zap },
            { label: "Send message", Icon: MessageSquare },
            { label: "Await reply", Icon: MessageSquare },
            { label: "Condition", Icon: GitBranch },
            { label: "Delay", Icon: Clock },
          ].map(({ label, Icon }) => (
            <span key={label} className="flex items-center gap-1.5 text-xs text-[#7B7B7B] font-semibold">
              <Icon size={13} /> {label}
            </span>
          ))}
        </div>

        {/* ── Body ────────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden">
          {/* Flow */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Trigger card — always first, always editable */}
            <div className={`mb-3 rounded-xl border-2 p-3 ${
              result ? (result.status === "trigger_not_matched"
                ? "border-amber-400 bg-amber-50" : "border-green-500 bg-white")
                : "border-gray-200 bg-white"}`}>
              <div className="mb-1 flex items-center gap-2">
                <Zap size={14} className="text-gray-500" />
                <span className="text-[11px] font-semibold tracking-wide text-gray-500">TRIGGER</span>
              </div>
              {result && (
                <p className={`mb-2 text-xs font-medium ${
                  result.status === "trigger_not_matched" ? "text-amber-700" : "text-green-700"}`}>
                  {result.status === "trigger_not_matched" ? "No match" : "Matched at 0:00"}
                </p>
              )}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">On Message:</label>
                <input
                  value={inbound}
                  onChange={(e) => setInbound(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleTryItOut(); }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-400 focus:outline-none"
                  placeholder="Type the message that should trigger this bot"
                />
              </div>
              <p className="mt-1 text-[11px] text-[#7B7B7B] font-semibold">
                Edit before running to test a keyword that shouldn't match
              </p>
            </div>

            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {!result && !error && (
              <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center">
                <p className="text-sm text-gray-500">
                  Press <span className="font-medium text-indigo-600">Try it out</span> to walk this flow.
                </p>
                <p className="mt-1 text-xs text-[#7B7B7B] font-semibold">
                  Nothing is sent — this is a dry run against your current canvas.
                </p>
              </div>
            )}

            {rows.map((row, i) =>
              row.kind === "single" ? (
                <StepCard key={row.step.id} step={row.step} body={bodyFor(row.step)}
                          delivery={sent.find((x: any) => x.step_id === row.step.id)} />
              ) : (
                // The two outcomes of a condition sit side by side so the
                // path NOT taken stays visible next to the one that ran —
                // stacking them reads as sequence, which is misleading.
                <div key={`branch_${i}`} className="mb-3 grid grid-cols-2 gap-3">
                  <StepCard step={row.a} branchLabel="BRANCH A" body={bodyFor(row.a)}
                            delivery={sent.find((x: any) => x.step_id === row.a.id)} />
                  <StepCard step={row.b} branchLabel="BRANCH B" body={bodyFor(row.b)}
                            delivery={sent.find((x: any) => x.step_id === row.b.id)} />
                </div>
              )
            )}
          </div>

          {/* Session panel */}
          <div className="w-72 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-gray-50 p-4">
            {result && (
              <div className="mb-3 flex items-center justify-between">
                <span className={`rounded-md px-2 py-1 text-xs font-medium ${
                  result.status === "waiting_for_reply" ? "bg-amber-100 text-amber-800"
                  : result.status === "completed" ? "bg-green-100 text-green-800"
                  : "bg-gray-200 text-gray-600"}`}>
                  {result.status === "waiting_for_reply" ? "Waiting for reply"
                    : result.status === "completed" ? "Completed" : "No match"}
                </span>
                <span className="font-mono text-xs text-[#7B7B7B] font-semibold">
                  {maskPhone(testPhone)}
                </span>
              </div>
            )}

            <p className="mb-2 text-sm font-medium text-gray-700">Session timeline</p>
            {!result && <p className="text-xs text-[#7B7B7B] font-semibold">Run the bot to see the timeline.</p>}
            <div className="space-y-2">
              {(result?.timeline ?? []).map((t, i) => (
                <div key={i} className="flex gap-2 text-xs">
                  <span className="w-9 flex-shrink-0 font-mono text-[#7B7B7B] font-semibold">{t.at}</span>
                  <span className={
                    t.tone === "warning" ? "text-amber-700"
                    : t.tone === "success" ? "text-gray-700" : "text-gray-500"}>
                    {t.text}
                  </span>
                </div>
              ))}
            </div>

            {/* What the recipient sent back. A quick-reply tap does not
                arrive as text, so the button's payload id is shown next to
                its label — that id is what a Branch step matches on. */}
            {result && (
              <div className="mt-4 border-t border-gray-200 pt-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Their replies</span>
                  <button
                    onClick={loadReplies}
                    className="text-[11px] text-indigo-600 transition-colors hover:text-indigo-700"
                  >
                    Refresh
                  </button>
                </div>
                {inboundReplies.length === 0 ? (
                  <p className="text-xs text-[#7B7B7B] font-semibold">
                    Nothing yet. Reply or tap a button on the handset, then Refresh.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {inboundReplies.map((r, i) => (
                      <div key={i} className="rounded-lg bg-white p-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                            {r.kind}
                          </span>
                          <span className="text-gray-800">{r.text || "(no text)"}</span>
                        </div>
                        {r.payload && (
                          <p className="mt-0.5 font-mono text-[10px] text-[#7B7B7B] font-semibold">id: {r.payload}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {result && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3 text-xs">
                <span className="text-gray-500">Session TTL</span>
                <span className={`font-mono ${
                  ttlLeft === 0 ? "font-medium text-red-600"
                  : (ttlLeft ?? ttl) <= 60 ? "text-amber-700"
                  : "text-gray-500"}`}>
                  {ttlLeft === 0
                    ? "expired"
                    : `${Math.floor((ttlLeft ?? ttl) / 60)}:${String((ttlLeft ?? ttl) % 60).padStart(2, "0")} left`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Reply bar — only meaningful while the bot is waiting ── */}
        <div className="border-t border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              value={draftReply}
              onChange={(e) => setDraftReply(e.target.value.slice(0, 60))}
              onKeyDown={(e) => { if (e.key === "Enter") handleSendReply(); }}
              disabled={!awaiting || loading}
              placeholder={awaiting ? awaiting.prompt : "The bot isn't waiting for a reply"}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
            />
            <button
              onClick={handleSendReply}
              disabled={!awaiting || !draftReply.trim() || loading}
              className="flex items-center gap-1.5 rounded-lg border border-indigo-400 bg-white px-4 py-2 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-50"
            >
              <CornerDownLeft size={14} /> Send reply
            </button>
          </div>
          <p className="mt-1 text-[11px] text-[#7B7B7B] font-semibold">
            Types a reply on the recipient's behalf to walk the flow forward. Their real replies appear on the right.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreviewBotPanel;